// Shared types + palette for the Machinery Cost Analysis report.
// Machinery cost is triple-source: 'NORM' = applied machinery norms (grain =
// machineryNormId, activity-scoped — the item carries its activity), 'TOOLS'
// = task tools (grain = toolId + workUomId) and 'EQUIPMENT' = applied
// deployment norms (grain = deploymentNormId, live-derived — synthetic head
// 'EQUIPMENT', per-machine split at drill level). On top of the itemized
// sources the summary carries a consumablesCost bucket (tool split materials)
// that is part of the total but never itemized. There is no single id across
// sources, so row identity = source + conditional ids.
// All decimals arrive as strings and gRPC may omit zero-valued fields, so
// every reader goes through `num` and defaults missing values to 0. The
// EQUIPMENT fields are additive — an older backend simply never sends them
// and the report renders exactly as before.

export {
  num,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  perUnit,
  computeParetoAbc,
} from "../ThreeMConsolidation/threeMShared";

export type MachinerySource = "NORM" | "TOOLS" | "EQUIPMENT";

export interface MachinerySummaryRow {
  source: MachinerySource;
  machineryNormId?: number;
  toolId?: number;
  workUomId?: number;
  /** EQUIPMENT rows key on the deployment norm (other ids arrive as 0). */
  deploymentNormId?: number;
  /** Machinery head (category string, e.g. MUCKING), 'TOOLS' or 'EQUIPMENT'. */
  head?: string;
  itemName?: string;
  itemCode?: string;
  activityId?: number;
  /** Machinery norms are activity-scoped; '' for TOOLS rows. */
  activityName?: string;
  uomName?: string;
  totalQuantity?: string;
  avgRate?: string;
  totalCost?: string;
  taskCount?: number;
  lineCount?: number;
  /**
   * EQUIPMENT rows only: what avgRate is made of, head by head. `perUnit` is
   * amount / totalQuantity and is ABSENT when the norm's matrix mixes output
   * units — the same condition that blanks uomName. Σ perUnit ≈ avgRate.
   */
  equipmentHeads?: Array<{ head: string; amount: string; perUnit?: string }>;
}

export interface MachinerySummaryData {
  projectName?: string;
  /** = normCost + toolsCost + equipmentCost + consumablesCost. */
  totalCost?: string;
  normCost?: string;
  toolsCost?: string;
  /** Applied deployment norms (live-derived); absent on older backends. */
  equipmentCost?: string;
  /** Tool split materials — included in the total, never itemized. */
  consumablesCost?: string;
  itemCount?: number;
  items?: MachinerySummaryRow[];
}

// Source family colours: Norm = geekblue, Tools = orange (mirrors manpower's
// PRW/Others split so the dual-source reports read the same way), Equipment =
// green (deployment norms). Consumables is a muted extra slice — a bucket,
// not a drillable source.
export const SOURCE_COLORS: Record<MachinerySource, string> = {
  NORM: "#2f54eb",
  TOOLS: "#fa8c16",
  EQUIPMENT: "#389e0d",
};

export const CONSUMABLES_COLOR = "#bfbfbf";

// Shade ramps for the donut's outer ring — item sub-slices stay inside their
// source's colour family so the ring reads as "parts of Norm / parts of
// Tools / parts of Equipment".
export const NORM_SHADES = ["#1d39c4", "#2f54eb", "#597ef7", "#85a5ff", "#adc6ff"];
export const TOOLS_SHADES = ["#d46b08", "#fa8c16", "#ffa940", "#ffc069", "#ffd591"];
export const EQUIPMENT_SHADES = ["#237804", "#389e0d", "#52c41a", "#73d13d", "#95de64"];

// antd Tag colours for the head chips: TOOLS is always orange (its source
// colour), EQUIPMENT is always green; norm heads cycle a cool palette so new
// heads pick a colour automatically as MACHINERY_HEADS grows.
const HEAD_TAG_COLORS = ["geekblue", "cyan", "purple", "magenta", "blue"];

export const headTagColor = (head?: string): string => {
  if (!head) return "default";
  if (head === "TOOLS") return "orange";
  if (head === "EQUIPMENT") return "green";
  // Stable colour per head name without importing the heads list here.
  let hash = 0;
  for (let i = 0; i < head.length; i += 1) {
    hash = (hash * 31 + head.charCodeAt(i)) % 997;
  }
  return HEAD_TAG_COLORS[hash % HEAD_TAG_COLORS.length];
};

// Stable identity for table keys and the breakdown drawer. gRPC omits
// zero-valued fields, hence the ?? 0 fallbacks (0 = NULL for TOOLS ids — the
// "Unlinked tool" row keys and drills down like any other).
export const machineryRowKey = (row: MachinerySummaryRow): string =>
  row.source === "NORM"
    ? `NORM-${row.machineryNormId ?? 0}`
    : row.source === "EQUIPMENT"
    ? `EQP-${row.deploymentNormId ?? 0}`
    : `TOOL-${row.toolId ?? 0}-${row.workUomId ?? 0}`;

// One server-built activity group of the paginated machinery register,
// straight from GET 3m-consolidation/machinery-activity-summary/:projectId.
// activityId 0 = the trailing "Tools & Machinery" pseudo-group (TOOLS rows
// carry no activity). uomName '' = the group's items disagree on UOM
// ("Mixed"). Decimals are strings and gRPC omits zero-valued fields — read
// through `num` with ?? 0 fallbacks.
export interface MachineryActivityGroup {
  /** 0 = Tools pseudo-group (items with no activity). */
  activityId?: number;
  activityName?: string;
  itemCount?: number;
  totalQuantity?: string;
  /** Cost-weighted average: group cost ÷ group quantity. */
  avgRate?: string;
  /** Single UOM when every item agrees, '' = mixed. */
  uomName?: string;
  totalCost?: string;
  taskCount?: number;
  lineCount?: number;
  /** The group's machinery items, cost DESC. */
  items?: MachinerySummaryRow[];
}

// One flat row of the all-items task breakdown (Excel export): the per-item
// drill-down rows plus the summary identity (ids arrive as 0 for NULL, so
// machineryRowKey works on these rows too), straight from
// GET 3m-consolidation/machinery-breakdown-all/:projectId.
export interface MachineryBreakdownAllRow {
  source: MachinerySource;
  machineryNormId?: number;
  toolId?: number;
  workUomId?: number;
  deploymentNormId?: number;
  head?: string;
  itemName?: string;
  itemCode?: string;
  uomName?: string;
  phaseId?: number;
  phaseName?: string;
  activityId?: number;
  activityName?: string;
  taskId?: number;
  taskName?: string;
  taskSequenceId?: string;
  totalQuantity?: string;
  totalCost?: string;
  lineCount?: number;
}

// One (machinery item, month) cell of the all-items month grid, from
// GET 3m-consolidation/machinery-monthly-all/:projectId. Ids arrive as 0 for
// NULL, so machineryRowKey works on these rows too. `head` is the norm
// category, the literal 'TOOLS' for the task_tools arm, or 'EQUIPMENT' for
// the deployment-norm arm.
export interface MachineryMonthCellRow {
  source: MachinerySource;
  head?: string;
  machineryNormId?: number;
  toolId?: number;
  workUomId?: number;
  deploymentNormId?: number;
  month: string;
  monthQuantity?: string;
  monthCost?: string;
}
